import type { ColumnState, FilterModel } from "ag-grid-enterprise";
import type { ForecastTableControlValues } from "../components/tableControls/forecastTableControlValues";
import { parseVersionedPayload, sectionsPresent } from "./payloadCodec";

/** Payload schema version; bumped only by a shape change that needs migrating. */
export const SAVED_VIEW_VERSION = 1;

/**
 * The code-owned metric catalogues a row selection can start from. Not the
 * picker's list: these name *which KPI rows*, and other surfaces (the
 * range-plan modal's view strip) select from them directly.
 */
export type MetricPreset = "Full" | "OP" | "Compact" | "Per SQM";

/** How a view orders the grid. Resolved to AG Grid sort state on apply. */
export type ViewSortKey = "None" | "Trans" | "YoY";

/**
 * Controls a view asserts when applied. Partial because a built-in preset
 * speaks for a couple of them (OP plans by index) while a user's view
 * captures the whole bar — one merge either way. ``intake`` only types what
 * older views stored; it never applies (``canAssertShapeKey``).
 */
export type ViewDataShape = Partial<
  Pick<
    ForecastTableControlValues,
    "calendarView" | "timePeriod" | "planBy" | "planningYear" | "intake"
  >
>;

/**
 * Which rows a view shows, and in what order — stated semantically, never
 * as an AG Grid filter model. The metric set is resolved against live
 * metadata at apply time, so a preset tracks the release that owns it and a
 * saved view survives a calendar-family switch (which renames the year
 * column). Empty selections mean "everything".
 */
export interface ViewRows {
  /** Metric catalogue seeding the selection; null = every metric. */
  preset: MetricPreset | null;
  /** Metric categories unioned onto the preset (``metricCategoryMapping``). */
  categories: string[];
  /**
   * Metric names the user picked by hand, unioned onto the rest. A capture
   * also lands the grid's own metric filter here when it has drifted from
   * the preset, so a selection narrowed in the column menu can be saved.
   */
  metrics: string[];
  /**
   * Fiscal-year offsets to keep as ``[min, max]`` (0 = current year);
   * null = every year. Offsets rather than year names so a view doesn't
   * pin the season it was saved in.
   */
  yearOffsets: [number, number] | null;
  sortBy: ViewSortKey;
}

/**
 * Cosmetics and column layout a view restores. A capture always carries
 * the week labels; a built-in may leave them to the tenant.
 */
export interface ViewLayout extends Pick<
  ForecastTableControlValues,
  "gridStyleGrouping"
> {
  weekLabels?: ForecastTableControlValues["weekLabels"];
  /**
   * The user-chosen fields of each column's state (hide, width/flex,
   * pinned), or absent when the view was saved before the grid mounted.
   */
  columnState?: ColumnState[];
  /** Column filters set by hand, minus the columns the row selection owns. */
  filterModel?: FilterModel;
}

/**
 * A saved view's payload, the whole of ``control_state``. Every section is
 * optional: the save dialog's toggles decide which ones a view carries, and
 * a section it omits stays at whatever the user has now. What the grid
 * shows *of* — context, cubes — is not a view's business: context has its
 * own picker (``ContextViewPayload``), cubes are picked per session.
 */
export interface SavedViewPayload {
  version: number;
  dataShape?: ViewDataShape;
  rows?: ViewRows;
  layout?: ViewLayout;
}

/** Which sections a save captures. Mirrors {@link SavedViewPayload}. */
export interface ViewSections {
  /** Calendar family, period, plan-by, planning year. */
  dataShape: boolean;
  /** Metric selection, years, sort. */
  rows: boolean;
  /** Column widths / order / pinning / visibility, week labels, row styling. */
  layout: boolean;
}

/** Every section on, the shape a save modal opens with. */
export const ALL_VIEW_SECTIONS: ViewSections = {
  dataShape: true,
  rows: true,
  layout: true
};

/** No preset, no refinement — the state the bar starts in. */
export const DEFAULT_VIEW_ROWS: ViewRows = {
  preset: null,
  categories: [],
  metrics: [],
  yearOffsets: null,
  sortBy: "None"
};

export function sectionsOf(payload: SavedViewPayload): ViewSections {
  return sectionsPresent(payload, ALL_VIEW_SECTIONS);
}

export function parseSavedViewPayload(
  controlState: string | null | undefined
): SavedViewPayload | null {
  return parseVersionedPayload(controlState, SAVED_VIEW_VERSION);
}
